// Shared building blocks that emit the prototype's markup and classes (design/base.css). Nothing here invents a class.
import { useEffect, useId, useRef, type CSSProperties, type FormEvent, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { describeError, isApiError } from "../api/problem";
import { useFocusTrap } from "./focus-trap";
import { Icon } from "./icons";

export type Tone = "ok" | "warn" | "crit" | "info" | "neutral" | "accent";

export function Chip({ tone, dot, children, className }: { tone: Tone; dot?: boolean | undefined; children: ReactNode; className?: string | undefined }) {
  return <span className={`chip ${tone}${className ? ` ${className}` : ""}`}>{dot && <span className="dot" />}{children}</span>;
}

export function StatusChip({ status }: { status: string }) {
  const tone: Tone = status === "Active" ? "ok" : status === "Deactivated" || status === "Inactive" ? "crit" : status === "Invited" ? "info" : "neutral";
  return <Chip tone={tone} dot>{status}</Chip>;
}

export function Alert({ tone, children, actions, role }: { tone: "warn" | "crit" | "info" | "ok"; children: ReactNode; actions?: ReactNode; role?: "alert" | "status" | undefined }) {
  const icon = tone === "info" ? "info" : tone === "ok" ? "check" : "warning";
  return (
    <div className={`alert ${tone}`} role={role ?? (tone === "crit" ? "alert" : "status")}>
      <Icon name={icon} />
      <div className="grow">{children}</div>
      {actions && <div className="act">{actions}</div>}
    </div>
  );
}

/** One alert for any thrown error; a 403 says which permission is missing. */
export function ErrorAlert({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null;
  if (isApiError(error) && error.code === "PERMISSION_DENIED") {
    return <Alert tone="warn"><b>Not available to your role.</b> {typeof error.meta.permission === "string" ? `Needs ${error.meta.permission}.` : ""}</Alert>;
  }
  return <Alert tone="crit"><b>{isApiError(error) ? error.title : "Error"}</b> {describeError(error)}</Alert>;
}

export function Panel({ title, meta, head, children, foot, tight, className, id }: { title?: ReactNode; meta?: ReactNode; head?: ReactNode; children?: ReactNode; foot?: ReactNode; tight?: boolean | undefined; className?: string | undefined; id?: string | undefined }) {
  return (
    <div className={`panel${className ? ` ${className}` : ""}`} id={id}>
      {(title || head) && (
        <div className="panel-head">
          {title && <h2>{title}</h2>}
          {meta && <span className="meta">{meta}</span>}
          {head}
        </div>
      )}
      {children !== undefined && <div className={`panel-body${tight ? " tight" : ""}`}>{children}</div>}
      {foot && <div className="panel-foot">{foot}</div>}
    </div>
  );
}

export function Empty({ title, children, actions }: { title: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children && <span>{children}</span>}
      {actions && <div className="row c gap-8">{actions}</div>}
    </div>
  );
}

export function Field({ label, required, help, error, children, htmlFor }: { label: ReactNode; required?: boolean | undefined; help?: ReactNode; error?: string | undefined; children: ReactNode; htmlFor?: string | undefined }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}{required && <> <span className="req">*</span></>}</label>
      {children}
      {error && <div className="error" role="alert">{error}</div>}
      {help && !error && <div className="help">{help}</div>}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { mono?: boolean | undefined; error?: boolean | undefined; boxClass?: string | undefined };
export function TextInput({ mono, error, boxClass, ...rest }: InputProps) {
  return <div className={`input${mono ? " mono" : ""}${error ? " err" : ""}${rest.disabled ? " disabled" : ""}${boxClass ? ` ${boxClass}` : ""}`}><input {...rest} /></div>;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <div className="input ta"><textarea {...props} /></div>;
}

/** `boxStyle` styles the box the select sits in — e.g. a minimum width that fits its longest option. */
export function Select({ boxClass, boxStyle, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { boxClass?: string | undefined; boxStyle?: CSSProperties | undefined }) {
  return <div className={`input sel${rest.disabled ? " disabled" : ""}${boxClass ? ` ${boxClass}` : ""}`} style={boxStyle}><select {...rest}>{children}</select></div>;
}

/** `ariaLabel` names a box drawn without words beside it — a row's tick in a table (its row says what it ticks). */
export function Checkbox({ checked, onChange, children, disabled, name, ariaLabel }: { checked: boolean; onChange: (v: boolean) => void; children?: ReactNode; disabled?: boolean | undefined; name?: string | undefined; ariaLabel?: string | undefined }) {
  const id = useId();
  return (
    <label className="check" htmlFor={id}>
      <input id={id} type="checkbox" name={name} checked={checked} disabled={disabled} aria-label={ariaLabel} onChange={(e) => onChange(e.target.checked)} />
      <span className={`box${checked ? " on" : ""}`}>{checked && <Icon name="check" />}</span>
      {children}
    </label>
  );
}

export function Toggle({ on, label }: { on: boolean; label: string }) {
  return <span className={`toggle${on ? " on" : ""}`} role="switch" aria-checked={on} aria-label={label} />;
}

export function Avatar({ initials, size }: { initials: string; size?: "sm" | "lg" | undefined }) {
  return <span className={`avatar${size ? ` ${size}` : ""}`}>{initials}</span>;
}

export function KV({ rows, className }: { rows: [ReactNode, ReactNode][]; className?: string | undefined }) {
  return (
    <dl className={`kv${className ? ` ${className}` : ""}`}>
      {rows.map(([k, v], i) => (<KVRow key={i} k={k} v={v} />))}
    </dl>
  );
}
function KVRow({ k, v }: { k: ReactNode; v: ReactNode }) {
  return <><dt>{k}</dt><dd>{v}</dd></>;
}

export function Pager({ label, onPrev, onNext, hasPrev, hasNext }: { label: ReactNode; onPrev: () => void; onNext: () => void; hasPrev: boolean; hasNext: boolean }) {
  return (
    <div className="pager right">
      <button className="btn sm" type="button" disabled={!hasPrev} onClick={onPrev}>Previous</button>
      <span>{label}</span>
      <button className="btn sm" type="button" disabled={!hasNext} onClick={onNext}>Next</button>
    </div>
  );
}

/** `className` names a dialog that styles its own width or contents — the Adjust dialog's lines table (`adjust`). */
export function Modal({ title, meta, onClose, children, footer, wide, onSubmit, labelledBy, className }: { title: ReactNode; meta?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean | undefined; onSubmit?: ((e: FormEvent<HTMLFormElement>) => void) | undefined; labelledBy?: string | undefined; className?: string | undefined }) {
  const id = useId();
  const ref = useRef<HTMLFormElement>(null);
  useFocusTrap(ref);
  const close = useRef(onClose);
  close.current = onClose;
  // The first field takes the focus once, when the dialog opens. A parent re-rendering — Today's 60-second refresh — passes a
  // new onClose each time, and refocusing on that pulled the cursor out of whatever field was being typed in.
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>("input:not([disabled]), select, textarea, button")?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const titleId = labelledBy ?? `${id}-title`;
  return (
    <div className="backdrop">
      <form ref={ref} className={`modal${wide ? " wide" : ""}${className ? ` ${className}` : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={(e) => { e.preventDefault(); onSubmit?.(e); }}>
        <div className="panel-head"><h2 id={titleId}>{title}</h2>{meta && <span className="meta right">{meta}</span>}</div>
        <div className="panel-body">{children}</div>
        {footer && <div className="panel-foot">{footer}</div>}
      </form>
    </div>
  );
}

export function LockMark({ children = "Control PIN" }: { children?: ReactNode }) {
  return <span className="lockmark"><Icon name="lock" />{children}</span>;
}

export function Loading({ what }: { what: string }) {
  return <div className="loading" role="status">Loading {what}…</div>;
}
